import { UPLOAD_CONFIG } from './uploadConfig';

export function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** 后端入队背压错误码（T-A02 / 方案 §4.2） */
export const UPLOAD_QUEUE_FULL_CODE = 'UPLOAD_QUEUE_FULL';
export const UPLOAD_USER_BACKPRESSURE_CODE = 'UPLOAD_USER_BACKPRESSURE';
/** T-A07：插件 multipart 专用限流 */
export const PLUGIN_UPLOAD_RATE_LIMITED_CODE = 'PLUGIN_UPLOAD_RATE_LIMITED';

const BACKPRESSURE_CODES = new Set([
  UPLOAD_QUEUE_FULL_CODE,
  UPLOAD_USER_BACKPRESSURE_CODE,
  PLUGIN_UPLOAD_RATE_LIMITED_CODE,
]);

/** 用户可见文案：排队满 / 处理繁忙（勿用「系统繁忙」——会被 toast 静默吞掉） */
export const UPLOAD_BACKPRESSURE_USER_MESSAGE = '上传处理繁忙，请稍后重试';
export const UPLOAD_BACKPRESSURE_RETRYING_MESSAGE = '处理繁忙，正在自动重试…';

/** 背压场景默认最多重试次数（方案建议约 8；普通网络仍用 UPLOAD_CONFIG.maxRetries） */
export const UPLOAD_BACKPRESSURE_MAX_RETRIES = 8;

export function isUploadBackpressureError(error) {
  if (!error) return false;
  if (Number(error.status) === 429) return true;
  const code = String(error.code || error?.payload?.code || '');
  if (BACKPRESSURE_CODES.has(code)) return true;
  const message = String(error.message || error || '');
  return /UPLOAD_QUEUE_FULL|UPLOAD_USER_BACKPRESSURE|PLUGIN_UPLOAD_RATE_LIMITED|上传处理繁忙|排队已满|上传排队已满|插件上传过于频繁/i.test(message);
}

export function isRetryableUploadError(error) {
  const message = String(error?.message || error || '');
  if (error?.code === 'STORAGE_INSUFFICIENT' || error?.code === 'STORAGE_PLATFORM_FULL') {
    return false;
  }
  if (/容量不足|平台存储空间不足/.test(message)) {
    return false;
  }
  if (isUploadBackpressureError(error)) {
    return true;
  }
  if (Number(error?.status) === 503 || Number(error?.status) === 502 || Number(error?.status) === 504) {
    return true;
  }
  return /Network Error|timeout|ECONNABORTED|AbortError|网络|连接|socket|请求超时|上传处理超时/i.test(message);
}

export function formatUploadUserMessage(error) {
  if (isUploadBackpressureError(error)) {
    return UPLOAD_BACKPRESSURE_USER_MESSAGE;
  }
  const raw = String(error?.message || error || '').trim();
  return raw || '上传失败';
}

/**
 * 计算退避毫秒：优先 retryAfterSeconds，否则指数退避 + 抖动。
 */
export function resolveUploadRetryDelayMs(error, attempt, retryBaseMs = UPLOAD_CONFIG.retryBaseMs) {
  const base = Math.max(200, Number(retryBaseMs) || 1000);
  const retryAfter = Number(error?.retryAfterSeconds);
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    const fromServer = Math.round(retryAfter * 1000);
    return Math.max(fromServer, base) + Math.floor(Math.random() * 300);
  }
  const exp = base * (2 ** Math.max(0, attempt));
  return Math.min(exp, 15_000) + Math.floor(Math.random() * 400);
}

export function enrichUploadError(error, fallbackMessage = '上传失败') {
  const message = formatUploadUserMessage(error) || fallbackMessage;
  const err = new Error(message);
  if (error && typeof error === 'object') {
    if (error.status != null) err.status = error.status;
    if (error.code != null) err.code = error.code;
    if (error.retryAfterSeconds != null) err.retryAfterSeconds = error.retryAfterSeconds;
    if (error.payload != null) err.payload = error.payload;
    if (error.kind != null) err.kind = error.kind;
    if (Array.isArray(error.failedFiles)) err.failedFiles = error.failedFiles;
  }
  if (isUploadBackpressureError(error) && !err.code) {
    err.code = UPLOAD_QUEUE_FULL_CODE;
  }
  if (isUploadBackpressureError(error) && err.status == null) {
    err.status = 429;
  }
  err.cause = error;
  return err;
}

export async function runWithRetry(task, {
  maxRetries = UPLOAD_CONFIG.maxRetries,
  retryBaseMs = UPLOAD_CONFIG.retryBaseMs,
  backpressureMaxRetries = UPLOAD_BACKPRESSURE_MAX_RETRIES,
  onRetry,
} = {}) {
  let lastError = null;
  const networkLimit = Math.max(0, Number(maxRetries) || 0);
  const backpressureLimit = Math.max(networkLimit, Number(backpressureMaxRetries) || UPLOAD_BACKPRESSURE_MAX_RETRIES);

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await task();
    } catch (error) {
      lastError = error;
      if (error?.name === 'AbortError') {
        throw enrichUploadError(error, '上传已取消');
      }
      const backpressure = isUploadBackpressureError(error);
      const limit = backpressure ? backpressureLimit : networkLimit;
      const retryable = isRetryableUploadError(error);
      if (!retryable || attempt >= limit) {
        throw enrichUploadError(error);
      }
      const delayMs = resolveUploadRetryDelayMs(error, attempt, retryBaseMs);
      if (typeof onRetry === 'function') {
        onRetry({
          attempt: attempt + 1,
          maxRetries: limit,
          delayMs,
          error,
          message: backpressure ? UPLOAD_BACKPRESSURE_RETRYING_MESSAGE : formatUploadUserMessage(error),
        });
      }
      await sleep(delayMs);
    }
  }
}

/**
 * 分批上传：整批失败时逐张降级；支持压缩后的 File 列表。
 */
export async function uploadFilesInChunks({
  files,
  chunkSize = UPLOAD_CONFIG.chunkSize,
  shouldAbort = () => false,
  onChunkComplete,
  uploadChunk,
  uploadSingle,
}) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  let successCount = 0;
  let lastError = '';

  for (let index = 0; index < list.length; index += chunkSize) {
    if (shouldAbort()) break;

    const chunk = list.slice(index, index + chunkSize);

    try {
      const result = await uploadChunk(chunk);
      const saved = result?.saved ?? chunk.length;
      successCount += saved;
      if (result?.failed > 0) {
        lastError = result?.lastError || '部分图片上传失败';
      }
      onChunkComplete?.(Math.min(index + chunk.length, list.length), list.length);
    } catch (chunkError) {
      lastError = formatUploadUserMessage(chunkError) || '上传失败';
      for (let chunkIndex = 0; chunkIndex < chunk.length; chunkIndex += 1) {
        if (shouldAbort()) break;
        const file = chunk[chunkIndex];
        const globalIndex = index + chunkIndex;
        try {
          await uploadSingle(file, globalIndex);
          successCount += 1;
        } catch (error) {
          lastError = formatUploadUserMessage(error) || '上传失败';
        }
        onChunkComplete?.(globalIndex + 1, list.length);
      }
    }
  }

  return { successCount, lastError, total: list.length };
}
